import React, { useState } from 'react';
import { Sigma, ChevronDown } from 'lucide-react';
import { TaskBoardTask } from '../../types';
import { ColumnAggregationMode, COLUMN_AGGREGATION_LABELS } from '../../utils/projectConstants';
import { computeColumnNumericStats, formatAggregationNumber } from '../../utils/projectUtils';

interface ProjectColumnFooterProps {
  mode: ColumnAggregationMode;
  tasks: TaskBoardTask[];
  onChangeMode: (mode: ColumnAggregationMode) => void;
}

const MODES: ColumnAggregationMode[] = ['none', 'sum', 'average', 'both'];

const ProjectColumnFooter: React.FC<ProjectColumnFooterProps> = ({ mode, tasks, onChangeMode }) => {
  const [open, setOpen] = useState(false);
  const stats = computeColumnNumericStats(tasks);

  const showSum = mode === 'sum' || mode === 'both';
  const showAverage = mode === 'average' || mode === 'both';
  const hasDisplay = mode !== 'none' && stats && (showSum || showAverage);

  return (
    <div className="relative flex-shrink-0 mt-1.5 px-1">
      {hasDisplay ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full rounded-lg border border-gray-200/80 dark:border-gray-700/80 bg-white/80 dark:bg-gray-900/60 px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
        >
          <div className="flex items-center justify-between gap-1">
            <div className="min-w-0 space-y-0.5">
              {showSum && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  <span className="font-medium text-gray-600 dark:text-gray-300">Suma</span>{' '}
                  <span className="tabular-nums text-gray-800 dark:text-gray-100">
                    {formatAggregationNumber(stats.sum)}
                  </span>
                </p>
              )}
              {showAverage && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  <span className="font-medium text-gray-600 dark:text-gray-300">Prom.</span>{' '}
                  <span className="tabular-nums text-gray-800 dark:text-gray-100">
                    {formatAggregationNumber(stats.average)}
                  </span>
                </p>
              )}
            </div>
            <ChevronDown className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-2 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-white/50 dark:hover:bg-gray-900/40 transition-colors"
        >
          <Sigma className="w-3 h-3" />
          {mode === 'none' ? 'Cálculo' : 'Sin valores'}
        </button>
      )}

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 bottom-full mb-1 z-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1 text-xs">
            {MODES.map(option => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChangeMode(option);
                  setOpen(false);
                }}
                className={`w-full px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  mode === option ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {COLUMN_AGGREGATION_LABELS[option]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectColumnFooter;
